import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../api/client';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import {
  Plus,
  Trash2,
  Edit3,
  Users,
  Layers,
  Search,
  UserCheck,
  ChevronUp,
  ChevronsUpDown,
  X,
  UserPlus
} from 'lucide-react';

const listSchema = z.object({
  name: z.string().min(1, 'List name is required'),
  description: z.string().optional()
});

type ListFormValues = z.infer<typeof listSchema>;

export const Lists: React.FC = () => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean; title: string; message: string; onConfirm: () => void;
  }>({ open: false, title: '', message: '', onConfirm: () => {} });
  const openConfirm = (title: string, message: string, onConfirm: () => void) =>
    setConfirmDialog({ open: true, title, message, onConfirm });
  const closeConfirm = () => setConfirmDialog(d => ({ ...d, open: false }));

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingListId, setEditingListId] = useState<string | null>(null);
  const [editingListName, setEditingListName] = useState('');
  const [modalTab, setModalTab] = useState<'details' | 'members'>('details');

  // Sort state
  const [sortKey, setSortKey] = useState('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const toggleSort = (key: string) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  // Members modal state
  const [memberSearch, setMemberSearch] = useState('');
  const [addSearch, setAddSearch] = useState('');
  const [debouncedAddSearch, setDebouncedAddSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedAddSearch(addSearch), 300);
    return () => clearTimeout(t);
  }, [addSearch]);

  // Fetch lists with statistics
  const { data: lists = [], isLoading } = useQuery({
    queryKey: ['lists'],
    queryFn: async () => {
      const res = await apiClient.get('/lists');
      return res.data;
    }
  });

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors }
  } = useForm<ListFormValues>({
    resolver: zodResolver(listSchema)
  });

  // Save (Create/Update) mutation
  const saveMutation = useMutation({
    mutationFn: async (data: ListFormValues) => {
      if (isEditMode && editingListId) {
        const res = await apiClient.put(`/lists/${editingListId}`, data);
        return res.data;
      } else {
        const res = await apiClient.post('/lists', data);
        return res.data;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['lists']);
      queryClient.invalidateQueries(['recipients']); // update recipient filters
      showToast(
        isEditMode ? 'List updated successfully' : 'List created successfully', 
        'success'
      );
      setIsModalOpen(false);
      reset();
      setIsEditMode(false);
      setEditingListId(null);
    },
    onError: (err: any) => {
      const msg = err.response?.data?.error || 'Failed to save list';
      showToast(msg, 'error');
    }
  });

  // Fetch members of the list being edited
  const { data: membersData, isLoading: membersLoading } = useQuery({
    queryKey: ['listMembers', editingListId],
    queryFn: async () => {
      const res = await apiClient.get('/recipients', { params: { listId: editingListId, limit: 500 } });
      return res.data;
    },
    enabled: !!editingListId && modalTab === 'members'
  });
  const members: any[] = membersData?.recipients || [];

  // Search recipients not in this list (for add)
  const { data: searchData } = useQuery({
    queryKey: ['recipientSearch', debouncedAddSearch],
    queryFn: async () => {
      const res = await apiClient.get('/recipients', { params: { search: debouncedAddSearch, limit: 20, status: 'SUBSCRIBED' } });
      return res.data;
    },
    enabled: debouncedAddSearch.length >= 2
  });
  const searchResults: any[] = (searchData?.recipients || []).filter(
    (r: any) => !members.some((m: any) => m.id === r.id)
  );

  // Remove from list mutation
  const removeMutation = useMutation({
    mutationFn: async (recipientId: string) => {
      await apiClient.delete(`/lists/${editingListId}/recipients/${recipientId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['listMembers', editingListId]);
      queryClient.invalidateQueries(['lists']);
    },
    onError: () => showToast('Failed to remove recipient', 'error')
  });

  // Add to list mutation
  const addMutation = useMutation({
    mutationFn: async (recipientId: string) => {
      await apiClient.post('/recipients/bulk-add-list', { recipientIds: [recipientId], listId: editingListId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['listMembers', editingListId]);
      queryClient.invalidateQueries(['lists']);
      setAddSearch('');
    },
    onError: () => showToast('Failed to add recipient', 'error')
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiClient.delete(`/lists/${id}`);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['lists']);
      queryClient.invalidateQueries(['recipients']);
      showToast('List deleted successfully. Recipient associations cleared.', 'success');
    },
    onError: () => {
      showToast('Failed to delete list', 'error');
    }
  });

  const handleEditClick = (list: any, tab: 'details' | 'members' = 'details') => {
    setIsEditMode(true);
    setEditingListId(list.id);
    setEditingListName(list.name);
    setValue('name', list.name);
    setValue('description', list.description || '');
    setModalTab(tab);
    setMemberSearch('');
    setAddSearch('');
    setIsModalOpen(true);
  };

  const onSubmit = (data: ListFormValues) => {
    saveMutation.mutate(data);
  };

  const [search, setSearch] = useState('');
  const filtered = [...lists]
    .filter((l: any) => l.name.toLowerCase().includes(search.toLowerCase()))
    .sort((a: any, b: any) => {
      let av: any, bv: any;
      if (sortKey === 'name') { av = a.name?.toLowerCase(); bv = b.name?.toLowerCase(); }
      else if (sortKey === 'total') { av = a.totalRecipients || 0; bv = b.totalRecipients || 0; }
      else if (sortKey === 'active') { av = a.subscribedRecipients || 0; bv = b.subscribedRecipients || 0; }
      else { av = new Date(a.createdAt).getTime(); bv = new Date(b.createdAt).getTime(); }
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-900 dark:text-white tracking-tight uppercase">
            Recipient Lists
            {!isLoading && <span className="ml-2 text-sm font-normal text-slate-400 normal-case">({lists.length})</span>}
          </h2>
          <p className="text-xs text-slate-500 dark:text-slate-400">Segment subscribers into targeting categories.</p>
        </div>

        <div className="flex items-center gap-3">
          {/* Search */}
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <input
              type="text"
              placeholder="Search lists..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="form-input pl-8 pr-3 py-2 text-sm w-52"
            />
          </div>

          <button
            onClick={() => {
              setIsEditMode(false);
              setEditingListId(null);
              reset();
              setIsModalOpen(true);
            }}
            className="btn-primary flex items-center gap-2 shrink-0"
          >
            <Plus size={16} />
            Create List
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="glass-panel rounded-2xl overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-slate-200 dark:border-white/5">
              {[
                { key: 'name', label: 'List', cls: 'text-left px-5 py-3' },
                { key: 'total', label: 'Total', cls: 'text-center px-4 py-3' },
                { key: 'active', label: 'Active', cls: 'text-center px-4 py-3' },
                { key: 'createdAt', label: 'Created', cls: 'text-right px-4 py-3' },
              ].map(col => (
                <th key={col.key} className={`${col.cls} text-[11px] font-semibold text-slate-400 uppercase tracking-wider cursor-pointer select-none hover:text-slate-600 dark:hover:text-slate-300 transition-colors`} onClick={() => toggleSort(col.key)}>
                  <span className="inline-flex items-center gap-1">
                    {col.label}
                    {sortKey === col.key
                      ? <ChevronUp size={12} className={sortDir === 'desc' ? 'rotate-180 transition-transform' : 'transition-transform'} />
                      : <ChevronsUpDown size={12} className="opacity-40" />}
                  </span>
                </th>
              ))}
              <th className="text-right px-5 py-3 text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-white/5">
            {isLoading ? (
              <tr>
                <td colSpan={5} className="py-12 text-center">
                  <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto" />
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={5} className="py-12 text-center text-sm text-slate-400">
                  {search ? `No lists match "${search}"` : 'No lists yet. Click "Create List" to get started.'}
                </td>
              </tr>
            ) : filtered.map((list: any) => {
              const pct = list.totalRecipients > 0
                ? Math.round((list.subscribedRecipients / list.totalRecipients) * 100)
                : 0;
              return (
                <tr key={list.id} className="group hover:bg-slate-50 dark:hover:bg-white/[0.02] transition-colors cursor-pointer" onClick={() => navigate(`/lists/${list.id}`)}>
                  {/* List name + description */}
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-xl bg-primary-500/10 flex items-center justify-center text-primary-500 shrink-0">
                        <Layers size={16} />
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-slate-800 dark:text-white truncate">{list.name}</div>
                        {list.description && (
                          <div className="text-xs text-slate-400 truncate max-w-xs">{list.description}</div>
                        )}
                      </div>
                    </div>
                  </td>

                  {/* Total recipients */}
                  <td className="px-4 py-4 text-center">
                    <div className="flex items-center justify-center gap-1.5 text-slate-600 dark:text-slate-300">
                      <Users size={13} className="text-slate-400" />
                      <span className="text-sm font-semibold tabular-nums">{list.totalRecipients || 0}</span>
                    </div>
                  </td>

                  {/* Active / subscribed with mini progress */}
                  <td className="px-4 py-4 text-center">
                    <div className="flex flex-col items-center gap-1">
                      <div className="flex items-center gap-1.5 text-accent-emerald">
                        <UserCheck size={13} />
                        <span className="text-sm font-semibold tabular-nums">{list.subscribedRecipients || 0}</span>
                      </div>
                      <div className="w-16 h-1 rounded-full bg-slate-100 dark:bg-white/5 overflow-hidden">
                        <div
                          className="h-full rounded-full bg-emerald-400"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <span className="text-[10px] text-slate-400">{pct}%</span>
                    </div>
                  </td>

                  {/* Created date */}
                  <td className="px-4 py-4 text-right text-xs text-slate-400 whitespace-nowrap">
                    {new Date(list.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                  </td>

                  {/* Actions */}
                  <td className="px-5 py-4 text-right" onClick={e => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => navigate(`/lists/${list.id}`)}
                        className="p-1.5 rounded-lg text-slate-400 hover:text-primary-500 hover:bg-slate-100 dark:hover:bg-white/5 transition-colors"
                        title="View Members"
                      >
                        <Users size={14} />
                      </button>
                      <button
                        onClick={() => handleEditClick(list, 'details')}
                        className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-white/5 transition-colors"
                        title="Edit List"
                      >
                        <Edit3 size={14} />
                      </button>
                      {user?.role === 'ADMIN' && (
                        <button
                          onClick={() => openConfirm(
                            'Delete List',
                            `Delete "${list.name}"? All recipient associations will be removed.`,
                            () => deleteMutation.mutate(list.id)
                          )}
                          className="p-1.5 rounded-lg text-slate-400 hover:text-accent-rose hover:bg-slate-100 dark:hover:bg-white/5 transition-colors"
                          title="Delete List"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false); reset(); setIsEditMode(false); setEditingListId(null); }}
        title={isEditMode ? `Edit: ${editingListName}` : 'Create Recipient List'}
      >
        {/* Tabs — only in edit mode */}
        {isEditMode && (
          <div className="flex border-b border-slate-200 dark:border-white/5 mb-5 -mt-1">
            {(['details', 'members'] as const).map(tab => (
              <button
                key={tab}
                type="button"
                onClick={() => setModalTab(tab)}
                className={`px-4 py-2.5 text-xs font-semibold capitalize transition-colors border-b-2 -mb-px ${
                  modalTab === tab
                    ? 'text-primary-500 border-primary-500'
                    : 'text-slate-400 border-transparent hover:text-slate-600 dark:hover:text-slate-300'
                }`}
              >
                {tab === 'members' ? `Members` : 'Details'}
              </button>
            ))}
          </div>
        )}

        {/* Details tab */}
        {modalTab === 'details' && (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">List Name</label>
              <input
                type="text"
                placeholder="VIP Customers"
                {...register('name')}
                className="form-input"
              />
              {errors.name && <span className="text-xs text-accent-rose">{errors.name.message}</span>}
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Description (Optional)</label>
              <textarea
                rows={3}
                placeholder="Subscribers who bought packages during summer promos..."
                {...register('description')}
                className="form-input"
              />
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-slate-200 dark:border-white/5">
              <button type="button" onClick={() => { setIsModalOpen(false); reset(); setIsEditMode(false); setEditingListId(null); }} className="btn-secondary">Cancel</button>
              <button type="submit" disabled={saveMutation.isLoading} className="btn-primary">
                {saveMutation.isLoading ? 'Saving...' : 'Save List'}
              </button>
            </div>
          </form>
        )}

        {/* Members tab */}
        {modalTab === 'members' && (
          <div className="flex flex-col gap-4" style={{ minHeight: 420 }}>
            {/* Add recipient search */}
            <div>
              <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider block mb-1.5">Add Recipient</label>
              <div className="relative">
                <UserPlus size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                <input
                  type="text"
                  placeholder="Search by email or name..."
                  value={addSearch}
                  onChange={e => setAddSearch(e.target.value)}
                  className="form-input pl-8 pr-3 py-2 text-sm w-full"
                />
              </div>
              {debouncedAddSearch.length >= 2 && (
                <div className="mt-1 rounded-xl border border-slate-200 dark:border-white/10 overflow-hidden bg-white dark:bg-slate-900 max-h-40 overflow-y-auto">
                  {searchResults.length === 0 ? (
                    <div className="px-4 py-3 text-xs text-slate-400">No results or already in list</div>
                  ) : searchResults.map((r: any) => (
                    <div key={r.id} className="flex items-center justify-between px-3 py-2 hover:bg-slate-50 dark:hover:bg-white/5 transition-colors">
                      <div>
                        <div className="text-sm font-medium text-slate-700 dark:text-slate-200">{r.email}</div>
                        {(r.firstName || r.lastName) && <div className="text-xs text-slate-400">{r.firstName} {r.lastName}</div>}
                      </div>
                      <button
                        type="button"
                        onClick={() => addMutation.mutate(r.id)}
                        disabled={addMutation.isLoading}
                        className="text-xs btn-primary px-2.5 py-1"
                      >
                        Add
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Existing members list */}
            <div className="flex-1 flex flex-col gap-2 min-h-0">
              <div className="flex items-center justify-between">
                <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                  Members {!membersLoading && `(${members.length})`}
                </label>
                <div className="relative">
                  <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                  <input
                    type="text"
                    placeholder="Filter members..."
                    value={memberSearch}
                    onChange={e => setMemberSearch(e.target.value)}
                    className="form-input pl-7 pr-3 py-1.5 text-xs w-44"
                  />
                </div>
              </div>

              <div className="flex-1 rounded-xl border border-slate-200 dark:border-white/5 overflow-y-auto" style={{ maxHeight: 260 }}>
                {membersLoading ? (
                  <div className="py-8 flex justify-center">
                    <div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : members.filter((m: any) =>
                    !memberSearch || m.email.toLowerCase().includes(memberSearch.toLowerCase()) ||
                    `${m.firstName} ${m.lastName}`.toLowerCase().includes(memberSearch.toLowerCase())
                  ).length === 0 ? (
                  <div className="py-8 text-center text-sm text-slate-400">No members found</div>
                ) : members
                    .filter((m: any) =>
                      !memberSearch || m.email.toLowerCase().includes(memberSearch.toLowerCase()) ||
                      `${m.firstName} ${m.lastName}`.toLowerCase().includes(memberSearch.toLowerCase())
                    )
                    .map((m: any) => (
                  <div key={m.id} className="flex items-center justify-between px-3 py-2.5 border-b border-slate-100 dark:border-white/5 last:border-0 hover:bg-slate-50 dark:hover:bg-white/[0.02] transition-colors group">
                    <div className="min-w-0">
                      <div className="text-sm text-slate-700 dark:text-slate-200 truncate">{m.email}</div>
                      {(m.firstName || m.lastName) && (
                        <div className="text-xs text-slate-400">{m.firstName} {m.lastName}</div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0 ml-2">
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                        m.status === 'SUBSCRIBED' ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' :
                        m.status === 'UNSUBSCRIBED' ? 'bg-red-500/10 text-red-500' :
                        'bg-slate-100 dark:bg-white/5 text-slate-400'
                      }`}>{m.status}</span>
                      <button
                        type="button"
                        onClick={() => openConfirm(
                          'Remove from List',
                          `Remove ${m.email} from this list?`,
                          () => removeMutation.mutate(m.id)
                        )}
                        className="opacity-0 group-hover:opacity-100 p-1 rounded-lg text-slate-400 hover:text-accent-rose hover:bg-slate-100 dark:hover:bg-white/5 transition-all"
                        title="Remove from list"
                      >
                        <X size={13} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </Modal>
      <ConfirmDialog
        isOpen={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmLabel="Delete"
        onConfirm={() => { confirmDialog.onConfirm(); closeConfirm(); }}
        onCancel={closeConfirm}
      />
    </div>
  );
};
export default Lists;
